import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { publicGet, publicPost } from '../api/client'

type TokenInfo = { valid: true; tenant_name: string } | { valid: false; message: string }

type FormState = {
  first_name: string
  last_name: string
  email: string
  date_of_birth: string
  consent_sms: boolean
  consent_email: boolean
  consent_whatsapp: boolean
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.375rem',
  fontSize: '0.95rem',
  color: '#111827',
  background: '#fff',
  boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '0.3rem',
}

export default function OnboardingPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null)
  const [isValidating, setIsValidating] = useState(true)

  const [form, setForm] = useState<FormState>({
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    consent_sms: false,
    consent_email: false,
    consent_whatsapp: false,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!token) {
      setTokenInfo({ valid: false, message: 'No token found in this link.' })
      setIsValidating(false)
      return
    }
    publicGet<{ valid: true; tenant_name: string }>(`/api/v1/onboarding/${token}`)
      .then((data) => setTokenInfo(data))
      .catch((err: Error) => setTokenInfo({ valid: false, message: err.message }))
      .finally(() => setIsValidating(false))
  }, [token])

  function set(field: keyof FormState, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    setIsSubmitting(true)
    try {
      await publicPost(`/api/v1/onboarding/${token}/complete`, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        date_of_birth: form.date_of_birth,
        consent: {
          sms: form.consent_sms,
          email: form.consent_email,
          whatsapp: form.consent_whatsapp,
        },
      })
      setSuccess(true)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: '#f9fafb',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '3rem 1rem',
    fontFamily: 'system-ui, sans-serif',
  }

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: '0.75rem',
    border: '1px solid #e5e7eb',
    padding: '2rem',
    width: '100%',
    maxWidth: '480px',
  }

  if (isValidating) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <p style={{ color: '#6b7280', textAlign: 'center' }}>Validating your link…</p>
        </div>
      </div>
    )
  }

  if (!tokenInfo?.valid) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ marginTop: 0, color: '#dc2626' }}>Link unavailable</h2>
          <p style={{ color: '#6b7280' }}>
            {(tokenInfo as { valid: false; message: string } | null)?.message ?? 'This link is not valid.'}
          </p>
          <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
            Please contact your broker to request a new link.
          </p>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✓</div>
            <h2 style={{ marginTop: 0, color: '#111827' }}>You're all set</h2>
            <p style={{ color: '#6b7280' }}>
              Your details have been saved. Your broker at {tokenInfo.tenant_name} will be in touch shortly.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ marginTop: 0, color: '#111827', marginBottom: '0.25rem' }}>
          Set up your client account
        </h2>
        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: 0, marginBottom: '1.5rem' }}>
          {tokenInfo.tenant_name} has invited you to complete your details.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>First name</label>
              <input
                style={inputStyle}
                type="text"
                value={form.first_name}
                onChange={(e) => set('first_name', e.target.value)}
                required
                autoComplete="given-name"
              />
            </div>
            <div>
              <label style={labelStyle}>Last name</label>
              <input
                style={inputStyle}
                type="text"
                value={form.last_name}
                onChange={(e) => set('last_name', e.target.value)}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Email address</label>
            <input
              style={inputStyle}
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>Date of birth</label>
            <input
              style={inputStyle}
              type="date"
              value={form.date_of_birth}
              onChange={(e) => set('date_of_birth', e.target.value)}
              required
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div style={{
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1.5rem',
          }}>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
              Communications preferences
            </p>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
              Please select how you'd like {tokenInfo.tenant_name} to contact you.
            </p>

            {([
              { key: 'consent_sms' as const,       label: 'SMS text messages' },
              { key: 'consent_email' as const,      label: 'Email' },
              { key: 'consent_whatsapp' as const,   label: 'WhatsApp' },
            ] as const).map(({ key, label }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form[key]}
                  onChange={(e) => set(key, e.target.checked)}
                  style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.9rem', color: '#374151' }}>{label}</span>
              </label>
            ))}
          </div>

          {submitError && (
            <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '1rem' }}>{submitError}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '0.65rem',
              background: isSubmitting ? '#93c5fd' : '#1d4ed8',
              color: '#fff',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Saving…' : 'Complete setup'}
          </button>
        </form>
      </div>
    </div>
  )
}
